import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { CriminalAppellateCoverSubmitter } from '../../services/criminalAppellateCaseRequests';
import type { CriminalAppellateWizardDraft } from '../../types/criminalAppellateCaseRequest';
import type { CriminalSaveStep4 } from '../../types/criminalApi';
import { formatDisplayDate, formatDisplayDateTime } from '../../utils/dateFormat';
import { useCriminalAppellateLookups } from '../wizard/criminalAppellate/useCriminalAppellateLookups';
import { apiDisplayValue, apiOrLookupLabel, deliveryMethodDisplay, submitterFromApiStep } from './viewRequestHelpers';
import type { CriminalWizardDraft } from '../../types/criminalCaseRequest';
import {
  ReviewChips,
  ReviewField,
  ReviewGrid,
  ReviewSection,
  ReviewSubBlock,
} from '../wizard/criminal/CriminalReviewUi';
import { ConnectedAgdFileRefReviewField } from '../../components/view-request/ConnectedAgdFileRefReviewField';

type Props = {
  caseRequestId: string;
  draft: CriminalAppellateWizardDraft;
  submittedAt: string;
  step4?: CriminalSaveStep4;
  coverSubmitter?: CriminalAppellateCoverSubmitter;
};

export default function ViewRequestCriminalAppellateCaseDetailsTab({
  caseRequestId,
  draft,
  submittedAt,
  step4,
  coverSubmitter,
}: Props) {
  const { t } = useTranslation();
  const noData = t('common.noDataFound');
  const lookups = useCriminalAppellateLookups('review', draft.provinceId, draft.districtId);

  const appealMatterLabel =
    draft.appealMatter === 'yes' ? t('common.yes') : draft.appealMatter === 'no' ? t('common.no') : noData;

  const connectedLabel =
    draft.hasConnectedMatters === 'yes' ? t('common.yes') : draft.hasConnectedMatters === 'no' ? t('common.no') : noData;

  const clientTypeLabel =
    draft.clientType === 'corporation'
      ? 'Public Corporation / Statutory Board / State Owned Entity / Other Incorporated Body'
      : 'Government Ministry / Department / Other Unincorporated Body';

  const deliveryLabel = useMemo(
    () => deliveryMethodDisplay(draft as unknown as CriminalWizardDraft, lookups.deliveryMethods, step4),
    [draft, lookups.deliveryMethods, step4],
  );

  const submitter = useMemo(() => {
    const fromApi = submitterFromApiStep(step4);
    return {
      agency: fromApi.agency !== '—' ? fromApi.agency : apiDisplayValue(coverSubmitter?.agency),
      division: fromApi.division !== '—' ? fromApi.division : apiDisplayValue(coverSubmitter?.division),
      designation: fromApi.designation,
      name: fromApi.name,
      email: fromApi.email,
      contact: fromApi.contact,
    };
  }, [step4, coverSubmitter]);

  return (
    <div className="vcr-case-details">
      <p className="vcr-submitted-at">
        {t('viewRequest.requestSubmittedAt')} <strong>{formatDisplayDateTime(submittedAt) || noData}</strong>
      </p>

      <ReviewSection step="Step 01" title="Case Overview">
        <ReviewSubBlock title="Case Intake Details">
          <ReviewGrid cols={4}>
            <ReviewField label="File Category" value={draft.fileCategory} />
            <ReviewField label="Sub File Category" value={draft.subFileCategoryName} />
          </ReviewGrid>
          <ReviewChips items={draft.applicableAreas} />
          <ReviewField label="Description" value={draft.description} fullWidth />
          <ReviewGrid cols={4}>
            <ReviewField label="Appeal Matter" value={appealMatterLabel} />
            {draft.appealMatter === 'yes' ? (
              <>
                <ReviewField label="Original Court" value={draft.originalCourt} />
                <ReviewField
                  label="Judgement / Order"
                  value={draft.judgementOrderOriginalFileName || draft.judgementOrderFileName}
                />
              </>
            ) : null}
            <ReviewField
              label="Province"
              value={apiOrLookupLabel(draft.provinceName, draft.provinceId, lookups.provinces)}
            />
            <ReviewField
              label="District"
              value={apiOrLookupLabel(draft.districtName, draft.districtId, lookups.districts)}
            />
            <ReviewField
              label="Police Station"
              value={apiOrLookupLabel(draft.policeStationName, draft.policeStationId, lookups.policeStations)}
            />
            <ReviewField label="Your Reference Number (External)" value={draft.yourReferenceNumber} />
            <ReviewField label="Other External Reference Number(s)" value={draft.otherExternalReferences} />
            <ReviewField label="Case Number" value={draft.caseNumber} />
            <ReviewField label="Are there any connected matters (Cases)?" value={connectedLabel} />
            {draft.hasConnectedMatters === 'yes' ? (
              <>
                <ReviewField label="Connected Case Number" value={draft.connectedCaseNumber} />
                <ConnectedAgdFileRefReviewField
                  caseRequestId={caseRequestId}
                  label="Connected AGD File Reference Number"
                  fileRef={draft.connectedAgdFileRef}
                />
              </>
            ) : null}
            {draft.instituteType === 'corporation' && draft.corporateInstitute ? (
              <>
                <ReviewField
                  label="Corporate Institute"
                  value={draft.corporateInstituteName || draft.corporateInstitute}
                />
                {draft.corporateInstituteOther ? (
                  <ReviewField label="Other Institute Name" value={draft.corporateInstituteOther} />
                ) : null}
              </>
            ) : null}
          </ReviewGrid>
        </ReviewSubBlock>
      </ReviewSection>

      <ReviewSection step="Step 02" title="Party Details">
        <ReviewSubBlock title="Party Details">
          <ReviewGrid cols={4}>
            <ReviewField label="Plaintiff / Applicant Name" value={draft.plaintiffApplicantName} />
            <ReviewField label="Defendant / Respondent Name" value={draft.defendantRespondentName} />
            <ReviewField label="Select one of the following" value={clientTypeLabel} span={2} />
            {draft.otherInvolvedInstitutions ? (
              <ReviewField label="Other Involved Institutions" value={draft.otherInvolvedInstitutions} fullWidth />
            ) : null}
            <ReviewField label="Name of the Legal Officer / Officer In-Charge" value={draft.legalOfficerName} />
            <ReviewField label="Contact Number" value={draft.legalOfficerContact} />
          </ReviewGrid>
        </ReviewSubBlock>
      </ReviewSection>

      <ReviewSection step="Step 03" title="Timeline">
        <ReviewSubBlock title="Timeline">
          <ReviewGrid cols={3}>
            <ReviewField
              label="Urgency Level"
              value={apiOrLookupLabel(draft.urgencyLevelName, draft.urgencyLevelId, lookups.urgencyLevels)}
            />
            <ReviewField label="Deadline" value={formatDisplayDate(draft.deadline)} />
            <ReviewField label="Urgency Justification" value={draft.urgencyJustification} />
          </ReviewGrid>
        </ReviewSubBlock>
      </ReviewSection>

      <ReviewSection step="Step 04" title="Submission & Physical File Delivery">
        <ReviewSubBlock title="Request Submitter's Details">
          <ReviewGrid cols={4}>
            <ReviewField label="Submitted Agency Name" value={submitter.agency} />
            <ReviewField label="Submitted Division/Unit" value={submitter.division} />
            <ReviewField label="Designation" value={submitter.designation} />
            <ReviewField label="Submitted by" value={submitter.name} />
            <span className="ccr-review-grid-spacer" aria-hidden="true" />
            <ReviewField label="Email Address" value={submitter.email} />
            <ReviewField label="Contact Number" value={submitter.contact} />
          </ReviewGrid>
        </ReviewSubBlock>
        <ReviewSubBlock title="Physical File Delivery Method">
          <ReviewField label="Physical File Delivery Method" value={deliveryLabel} fullWidth />
        </ReviewSubBlock>
      </ReviewSection>
    </div>
  );
}
